import { ParticipanteAta, ReuniaoComissao, SituacaoPresencaAta } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';

/** Tipo de documento gerado para preview/impressão da ata. */
export type TipoImpressaoAta = 'ata' | 'minuta';

/** Papel do participante na ata: membro (M) ou convidado (C). */
export type TipoParticipanteAta = 'M' | 'C';

/** Opção de presença/função (rótulo exibido + código persistido). */
export interface OpcaoPresencaAta {
    label: string;
    value: string;
}

/** Opções de presença/função para membros (inclui Coordenador/Secretário). */
export const OPCOES_PRESENCA_MEMBRO: OpcaoPresencaAta[] = [
    { label: 'Presente', value: 'P' },
    { label: 'Presente Virtual', value: 'V' },
    { label: 'Ausente', value: 'A' },
    { label: 'Ausente Justificado', value: 'J' },
    { label: 'Coordenador(a)', value: 'C' },
    { label: 'Secretário(a)', value: 'S' }
];

/** Opções de presença para convidados (sem funções de coordenação). */
export const OPCOES_PRESENCA_CONVIDADO: OpcaoPresencaAta[] = [
    { label: 'Presente', value: 'P' },
    { label: 'Presente Virtual', value: 'V' },
    { label: 'Ausente', value: 'A' },
    { label: 'Ausente Justificado', value: 'J' }
];

/** Normaliza texto para comparação (trim + maiúsculas). */
export function normalizarTextoAta(valor?: string | null): string {
    return (valor ?? '').trim().toUpperCase();
}

/** Código de presença/função efetivo do participante (função C/S tem precedência sobre presença). */
export function valorPresencaAta(participante: ParticipanteAta): string {
    const funcao = normalizarTextoAta(participante.inFuncao);
    if (funcao === 'C' || funcao === 'S') {
        return funcao;
    }
    return normalizarTextoAta(participante.inPresenca) || 'A';
}

/** Rótulo legível da presença do participante conforme o tipo (membro/convidado). */
export function rotuloPresencaAta(participante: ParticipanteAta, tipo: TipoParticipanteAta): string {
    const valor = valorPresencaAta(participante);
    const opcoes = tipo === 'M' ? OPCOES_PRESENCA_MEMBRO : OPCOES_PRESENCA_CONVIDADO;
    return opcoes.find((opcao) => opcao.value === valor)?.label || 'Ausente';
}

/** Ícone (PrimeIcons) correspondente à situação de presença. */
export function iconeSituacaoAta(situacao: SituacaoPresencaAta): string {
    switch (situacao) {
        case 'presente':
            return 'pi pi-check-circle';
        case 'virtual':
            return 'pi pi-video';
        case 'ausente':
            return 'pi pi-times-circle';
        case 'justificado':
            return 'pi pi-exclamation-circle';
        case 'secretario':
            return 'pi pi-book';
        case 'coordenador':
            return 'pi pi-star-fill';
        default:
            return 'pi pi-minus-circle';
    }
}

/** Classe CSS de cor correspondente à situação de presença. */
export function classeSituacaoAta(situacao: SituacaoPresencaAta): string {
    return `situacao-${situacao}`;
}

/** Converte o código de presença/função (P/V/A/J/C/S) na situação exibível. */
export function mapearOpcaoParaSituacaoAta(value: string): SituacaoPresencaAta {
    switch (value) {
        case 'P':
            return 'presente';
        case 'V':
            return 'virtual';
        case 'A':
            return 'ausente';
        case 'J':
            return 'justificado';
        case 'C':
            return 'coordenador';
        case 'S':
            return 'secretario';
        default:
            return 'ausente';
    }
}

/** Nomes já resolvidos dos assinantes (o componente resolve a partir de participantes/formulário). */
export interface AssinantesAta {
    coordenador: string;
    secretario: string;
    assistente: string;
}

/** Escapa os caracteres perigosos para interpolação segura em HTML. */
export function escapeHtml(texto: string): string {
    return texto.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

/**
 * Remove elementos e atributos perigosos de um HTML (script/iframe/handlers/js:) preservando a estrutura.
 * Usada antes de injetar o conteúdo da ata no documento de impressão (defesa em profundidade contra XSS).
 */
export function sanitizarHtmlDocumento(html: string): string {
    const documento = new DOMParser().parseFromString(html || '<p></p>', 'text/html');
    documento.querySelectorAll('script, iframe, object, embed, link, meta').forEach((elemento) => elemento.remove());
    documento.body.querySelectorAll('*').forEach((elemento) => {
        Array.from(elemento.attributes).forEach((atributo) => {
            const nome = atributo.name.toLowerCase();
            const valor = atributo.value.trim().toLowerCase();
            if (nome.startsWith('on') || valor.startsWith('javascript:')) {
                elemento.removeAttribute(atributo.name);
            }
        });
    });

    return documento.body.innerHTML || '<p></p>';
}

/** Data no formato dd/mm/aaaa (ou '-'). */
function formatarDataImpressao(data?: Date | null): string {
    if (!data) {
        return '-';
    }

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(data);
}

/** Data/hora por extenso usada no cabeçalho da minuta (ex.: "3 de JULHO de 2026, 14h:30m"). */
function formatarDataHoraMinuta(data?: Date | null): string {
    if (!data) {
        return '-';
    }

    const dia = new Intl.DateTimeFormat('pt-BR', { day: '2-digit' }).format(data);
    const mes = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(data).toUpperCase();
    const ano = new Intl.DateTimeFormat('pt-BR', { year: 'numeric' }).format(data);
    const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false }).format(data).replace(':', 'h:');
    return `${dia} de ${mes} de ${ano}, ${hora}m`;
}

/** Bloco de metadados (protocolo + quadro local/comissão/período) do cabeçalho da ata. */
function criarMetadadosImpressao(reuniao: ReuniaoComissao | null): string {
    return `<div class="protocolo-documento"><strong>N° Protocolo:</strong> ${escapeHtml(reuniao?.protocolo || '-')}</div>
<table class="quadro-reuniao">
    <tr>
        <td class="linha-local" colspan="2"><strong>Local:</strong> ${escapeHtml(reuniao?.local || '-')}</td>
    </tr>
    <tr>
        <td>DECCCT - COMISSÕES/CÂM. TÉCNICAS</td>
        <td class="comissao">${escapeHtml(reuniao?.nomeComissao || '-')}</td>
    </tr>
    <tr>
        <td class="linha-periodo"><strong>Início:</strong> ${escapeHtml(formatarDataImpressao(reuniao?.dataInicio))}</td>
        <td class="linha-periodo"><strong>Término:</strong> ${escapeHtml(formatarDataImpressao(reuniao?.dataTermino))}</td>
    </tr>
</table>`;
}

/** Cabeçalho institucional da ata (brasão + órgão + quadro de metadados). */
function criarCabecalhoAtaImpressao(reuniao: ReuniaoComissao | null): string {
    return `<header class="cabecalho-documento">
    <img class="brasao" src="/images/logos/brasao-republica.bmp" alt="">
    <div class="titulos-documento">
        <div class="orgao">CONSELHO FEDERAL DE MEDICINA</div>
        <div class="tipo-documento">ATA DE REUNIÃO</div>
    </div>
    <div></div>
</header>
    ${criarMetadadosImpressao(reuniao)}
<div class="barra-relatorio"><span>Relatório</span></div>`;
}

/** Cabeçalho da minuta (aviso de documento não-final + resumo da reunião). */
function criarCabecalhoMinutaImpressao(reuniao: ReuniaoComissao | null): string {
    const local = reuniao?.local ? `, ${reuniao.local}` : '';
    return `<header class="cabecalho-minuta">
    <h1 class="titulo-minuta">MINUTA DE ATA DE REUNIÃO</h1>
    <p class="aviso-minuta">IMPORTANTE: Este é um documento automático do Sistema para a revisão do conteúdo da ata de reunião. O layout deste documento NÃO reflete a versão final da Ata de Reunião que será assinada pelos membros.</p>
    <div class="resumo-minuta"><strong>Reunião:</strong> ${escapeHtml(reuniao?.nomeComissao || '-')} - ${escapeHtml(formatarDataHoraMinuta(reuniao?.dataInicio))}${escapeHtml(local)}</div>
</header>`;
}

/** Seção de assinaturas (coordenador/secretário/assistente) da ata. */
function criarSecaoAssinaturasImpressao(assinantes: AssinantesAta): string {
    return `<section class="assinaturas">
    <div>
        <div class="assinatura-nome">${escapeHtml(assinantes.coordenador)}</div>
        <div class="assinatura-cargo">Coordenador</div>
    </div>
    <div>
        <div class="assinatura-nome">${escapeHtml(assinantes.secretario)}</div>
        <div class="assinatura-cargo">Secretário</div>
    </div>
    <div class="assinatura-assistente">
        <div class="assinatura-nome">${escapeHtml(assinantes.assistente)}</div>
        <div class="assinatura-cargo">Assistente</div>
    </div>
</section>`;
}

/** Folha de estilos (impressão A4) compartilhada por ata e minuta. */
function estiloDocumentoImpressao(): string {
    return `@page {
            margin: 20mm 18mm;
        }
        * {
            box-sizing: border-box;
        }
        body {
            color: #000;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 13.5pt;
            line-height: 1.42;
            margin: 0;
        }
        .pagina {
            margin: 0 auto;
            max-width: 180mm;
        }
        .pagina-minuta {
            padding-top: 14mm;
        }
        .cabecalho-documento {
            align-items: flex-start;
            display: grid;
            grid-template-columns: 38mm 1fr 38mm;
            min-height: 42mm;
            padding-top: 12mm;
        }
        .brasao {
            display: block;
            height: 28mm;
            object-fit: contain;
            width: 28mm;
        }
        .titulos-documento {
            padding-top: 7mm;
            text-align: center;
        }
        .orgao {
            font-family: "Times New Roman", Times, serif;
            font-size: 16pt;
            font-weight: 700;
            margin-bottom: 8mm;
        }
        .tipo-documento {
            font-size: 14pt;
            font-weight: 400;
            text-transform: uppercase;
        }
        .protocolo-documento {
            font-family: "Times New Roman", Times, serif;
            font-size: 13.5pt;
            margin: 15mm 1mm 2mm 0;
            text-align: right;
        }
        .protocolo-documento strong {
            font-weight: 700;
        }
        .quadro-reuniao {
            border-collapse: collapse;
            font-size: 13pt;
            margin: 0 0 8mm;
            table-layout: fixed;
            width: 100%;
        }
        .quadro-reuniao td {
            border: 1px solid #111;
            padding: 5px 4px;
            vertical-align: top;
        }
        .quadro-reuniao .linha-local {
            background: #00b050;
            color: #fff;
            font-weight: 400;
        }
        .quadro-reuniao .linha-periodo {
            background: #92d050;
            color: #fff;
            font-weight: 400;
        }
        .quadro-reuniao strong {
            font-weight: 700;
        }
        .quadro-reuniao .comissao {
            line-height: 1.45;
        }
        .barra-relatorio {
            border: 1px solid #111;
            margin: 0 0 4mm;
            padding: 2px 3px;
        }
        .barra-relatorio span {
            background: #00b050;
            color: #fff;
            display: block;
            font-size: 13.5pt;
            font-weight: 700;
            padding: 2px 0;
            text-align: center;
        }
        main {
            min-height: 420px;
        }
        .conteudo-documento {
            margin-top: 0;
            text-align: justify;
        }
        .conteudo-documento p {
            margin: 0 0 6px;
        }
        .conteudo-documento strong,
        .conteudo-documento b {
            font-weight: 700;
        }
        .assinaturas {
            break-before: page;
            display: grid;
            grid-template-columns: 1fr 1fr;
            padding-top: 70mm;
            row-gap: 30mm;
            text-align: center;
        }
        .assinatura-assistente {
            grid-column: 1 / -1;
        }
        .assinatura-nome {
            font-size: 13pt;
            font-weight: 700;
            text-transform: uppercase;
        }
        .assinatura-cargo {
            font-size: 13pt;
            font-weight: 700;
            margin-top: 4px;
        }
        .somente-minuta {
            color: #222;
        }
        .cabecalho-minuta {
            margin: 0 0 12mm;
        }
        .titulo-minuta {
            font-size: 16pt;
            font-weight: 700;
            margin: 0 0 8mm;
            text-align: center;
            text-transform: uppercase;
        }
        .aviso-minuta {
            color: #f00;
            font-size: 12pt;
            font-weight: 700;
            line-height: 1.15;
            margin: 0 0 9mm;
            text-decoration: underline;
        }
        .resumo-minuta {
            font-size: 12pt;
            line-height: 1.25;
        }
        .resumo-minuta strong {
            font-weight: 700;
        }
        @media print {
            body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
            }
        }`;
}

/**
 * Gera o documento HTML completo (ata ou minuta) para preview/impressão.
 * O conteúdo do editor é sanitizado internamente antes de ser embutido.
 */
export function criarHtmlImpressaoDocumento(tipo: TipoImpressaoAta, reuniao: ReuniaoComissao | null, conteudoHtml: string, assinantes: AssinantesAta): string {
    const titulo = tipo === 'ata' ? 'Ata' : 'Minuta';
    const conteudoAta = sanitizarHtmlDocumento(conteudoHtml || '<p></p>');
    const cabecalho = tipo === 'ata' ? criarCabecalhoAtaImpressao(reuniao) : criarCabecalhoMinutaImpressao(reuniao);
    const assinaturas = tipo === 'ata' ? criarSecaoAssinaturasImpressao(assinantes) : '';

    return `<!doctype html>
<html lang="pt-BR">
<head>
    <meta charset="utf-8">
    <title>${escapeHtml(titulo)} - Reuniao ${escapeHtml(reuniao?.codigo ?? '')}</title>
    <style>
        ${estiloDocumentoImpressao()}
    </style>
</head>
<body>
    <div class="pagina ${tipo === 'minuta' ? 'pagina-minuta' : 'pagina-ata'}">
        ${cabecalho}
        <main>
            <section class="conteudo-documento${tipo === 'minuta' ? ' somente-minuta' : ''}">${conteudoAta}</section>
            ${assinaturas}
        </main>
    </div>
</body>
</html>`;
}
